/**
 * Deskstead — add-ons module ("Complete the Edge")
 * -----------------------------------------------------------------------------
 * Adds the hero product and any checked add-ons to the cart in ONE /cart/add.js
 * call, using the `items[]` payload.
 *
 * Why it intercepts rather than injecting hidden inputs: Horizon's
 * <product-form-component> builds its request with `new FormData(form)` and
 * posts `id` + `quantity`. Shopify does not document what happens when `id` and
 * `items[]` arrive together, so relying on that precedence would be building on
 * undefined behaviour. Instead this listens on the form in the CAPTURE phase —
 * which runs before the component's delegated `on:submit` handler on the
 * ancestor element — and takes over only when it has to.
 *
 * When nothing is checked it does not interfere at all: the event is left alone
 * and Horizon's native path runs untouched. That is the majority of purchases.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

class DsAddons extends HTMLElement {
  #form = null;
  #variantPrices = {};
  #abort = new AbortController();

  connectedCallback() {
    const raw = this.querySelector('[data-ds-addons-variants]')?.textContent;
    try {
      this.#variantPrices = raw ? JSON.parse(raw) : {};
    } catch {
      this.#variantPrices = {};
    }

    this.#form = this.closest('form[action*="/cart/add"]') ?? document.querySelector('form[action*="/cart/add"]');

    const { signal } = this.#abort;

    for (const box of this.#boxes()) {
      box.addEventListener('change', () => this.#render(), { signal });
    }

    for (const select of this.querySelectorAll('[data-ds-addon-variant]')) {
      select.addEventListener('change', () => this.#render(), { signal });
    }

    // Horizon rewrites the hidden `id` input when the variant changes; both the
    // change event and a direct attribute write need to refresh the total.
    const idInput = this.#idInput();
    if (idInput) {
      idInput.addEventListener('change', () => this.#render(), { signal });
      new MutationObserver(() => this.#render()).observe(idInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    // O seletor de pacote (blocks/ds-bundle.liquid) vive fora deste elemento, no
    // topo do bloco de compra. Um segundo interceptador de submit brigaria com
    // este, entao o bundle e lido daqui.
    for (const radio of document.querySelectorAll('[data-ds-bundle] input[type="radio"]')) {
      radio.addEventListener('change', () => this.#render(), { signal });
    }

    // O botao do Horizon e type="button" e vive FORA do form: clicar nele nao
    // dispara submit, o proprio tema adiciona o produto. Interceptar so o submit
    // deixava o Horizon adicionar o heroi de novo, e o carrinho saia com 2x.
    // Capturamos o clique no document, que roda antes de qualquer handler no
    // elemento, e cortamos a propagacao quando ha pacote ou add-on em jogo.
    document.addEventListener('click', this.#onClick, { capture: true, signal });
    this.#form?.addEventListener('submit', this.#onSubmit, { capture: true, signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @returns {HTMLInputElement[]} */
  #boxes() {
    return Array.from(this.querySelectorAll('[data-ds-addon]'));
  }

  #checked() {
    return this.#boxes().filter((b) => b.checked);
  }

  /**
   * Um add-on com mais de uma variante (o Desk Pad, com quatro tamanhos) carrega
   * um <select> na propria linha. Quando ele existe, e ele que manda no id e no
   * preco; o data-* do checkbox continua valendo para os add-ons de variante unica.
   */
  #variantSelect(box) {
    return box.closest('.ds-addons__row')?.querySelector('[data-ds-addon-variant]') ?? null;
  }

  #variantIdOf(box) {
    const select = this.#variantSelect(box);
    return select?.value || box.dataset.variantId;
  }

  #priceOf(box) {
    const select = this.#variantSelect(box);
    const option = select?.selectedOptions?.[0];
    return Number(option?.dataset.price ?? box.dataset.price) || 0;
  }

  #idInput() {
    return this.#form?.querySelector('input[name="id"]') ?? null;
  }

  #quantity() {
    const q = this.#form?.querySelector('[name="quantity"]');
    return Math.max(1, Number(q?.value) || 1);
  }

  /** Current main-variant unit price in cents. */
  #mainPrice() {
    const id = this.#idInput()?.value;
    return Number(this.#variantPrices[id]) || 0;
  }

  #bundlePick() {
    return document.querySelector('[data-ds-bundle] input[type="radio"]:checked');
  }

  #bundleIds() {
    const raw = this.#bundlePick()?.dataset.extra || '';
    return raw.split(',').map((x) => x.trim()).filter(Boolean);
  }

  #bundleCents() {
    return Number(this.#bundlePick()?.dataset.extraCents) || 0;
  }

  #totalCents() {
    const base = this.#mainPrice() * this.#quantity();
    const comBundle = base + this.#bundleCents();
    return this.#checked().reduce((sum, b) => sum + this.#priceOf(b), comBundle);
  }

  #money(cents) {
    const currency = this.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #render() {
    const total = this.#totalCents();
    const count = this.#checked().length;

    const totalEl = this.querySelector('[data-ds-addons-total]');
    if (totalEl) totalEl.textContent = this.#money(total);

    const countEl = this.querySelector('[data-ds-addons-count]');
    if (countEl) countEl.textContent = count === 1 ? '1 added' : `${count} added`;

    for (const box of this.#boxes()) {
      const priceEl = box.closest('.ds-addons__row')?.querySelector('[data-ds-addon-price]');
      if (priceEl) priceEl.textContent = `+${this.#money(this.#priceOf(box))}`;
    }

    this.#renderButton(total);
  }

  /**
   * Horizon renders the button label as a bare text node between the icon and
   * the checkmark-burst span. Wrap it once so it can be updated without
   * disturbing either sibling, and re-wrap if the button gets re-rendered.
   */
  #renderButton(total) {
    const template = this.dataset.labelTemplate;
    if (!template) return;

    for (const button of document.querySelectorAll('.add-to-cart-button')) {
      let label = button.querySelector('[data-ds-atc-label]');

      if (!label) {
        const textNode = Array.from(button.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
        );
        if (!textNode) continue;
        label = document.createElement('span');
        label.setAttribute('data-ds-atc-label', '');
        textNode.replaceWith(label);
      }

      label.textContent = `${template} • ${this.#money(total)}`;
    }
  }

  #onClick = (event) => {
    const button = event.target?.closest?.('.add-to-cart-button');
    if (!button || button.disabled) return;
    if (!this.#temExtras()) return; // sem pacote e sem add-on, o Horizon cuida

    const variantId = this.#idInput()?.value;
    if (!variantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addToCart(this.#linhas(variantId));
  };

  #temExtras() {
    return this.#checked().length > 0 || this.#bundleIds().length > 0;
  }

  #linhas(variantId) {
    return [
      { id: Number(variantId), quantity: this.#quantity() },
      ...this.#bundleIds().map((id) => ({ id: Number(id), quantity: 1 })),
      ...this.#checked().map((b) => ({ id: Number(this.#variantIdOf(b)), quantity: 1 })),
    ];
  }

  #onSubmit = (event) => {
    // Rede de seguranca: so entra se algo disparar submit sem passar pelo clique.
    if (!this.#temExtras()) return;

    const variantId = this.#idInput()?.value;
    if (!variantId) return;

    event.preventDefault();
    event.stopPropagation();

    const lines = this.#linhas(variantId);

    this.#addToCart(lines);
  };

  async #addToCart(lines) {
    const buttons = Array.from(document.querySelectorAll('.add-to-cart-button'));
    buttons.forEach((b) => (b.disabled = true));

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines }),
      });

      const payload = await response.json();

      if (!response.ok) {
        deferred.reject?.(new Error(payload.description || 'Add to cart failed'));
        console.warn('[ds-addons] add to cart failed:', payload);
        return;
      }

      deferred.resolve?.(payload);
      for (const box of this.#boxes()) box.checked = false;
      this.#render();
    } catch (error) {
      deferred.reject?.(error);
      console.warn('[ds-addons] add to cart error:', error);
    } finally {
      buttons.forEach((b) => (b.disabled = false));
    }
  }
}

if (!customElements.get('ds-addons')) {
  customElements.define('ds-addons', DsAddons);
}
